import React, { useState } from 'react';
import { Clock, User, ChevronDown, ChevronUp, Target, BookOpen } from 'lucide-react';

const PRIORITY_CONFIG = {
    CRITICAL: {
        label: 'Critical',
        bg: 'bg-red-50',
        border: 'border-red-200',
        text: 'text-red-700',
        dot: 'bg-red-500',
        badge: 'bg-red-100 text-red-700 border-red-200',
    },
    HIGH: {
        label: 'High',
        bg: 'bg-orange-50',
        border: 'border-orange-200',
        text: 'text-orange-700',
        dot: 'bg-orange-500',
        badge: 'bg-orange-100 text-orange-700 border-orange-200',
    },
    MEDIUM: {
        label: 'Medium',
        bg: 'bg-yellow-50',
        border: 'border-yellow-200',
        text: 'text-yellow-700',
        dot: 'bg-yellow-400',
        badge: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    },
    LOW: {
        label: 'Low',
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        text: 'text-blue-700',
        dot: 'bg-blue-400',
        badge: 'bg-blue-100 text-blue-700 border-blue-200',
    },
};

/**
 * Derive priority from indicator level + timeline text.
 */
export const getPriority = (indicatorLevel, timelineText = '') => {
    if (indicatorLevel === 1) return 'CRITICAL';
    const tl = (timelineText || '').toLowerCase();
    if (tl.includes('short') || tl.includes('immediate') || tl.includes('urgent')) return 'HIGH';
    if (tl.includes('medium') || tl.includes('quarterly') || tl.includes('q1') || tl.includes('q2')) return 'MEDIUM';
    return 'LOW';
};

/**
 * ActionTaskCard — displays one recommendation from an action plan.
 *
 * Props:
 *   action           - { action, timeline, responsibility, resource }
 *   indicatorLevel   - 1-4 (used to compute priority)
 *   kpi              - string (shown once per indicator, passed from parent)
 *   showKpi          - bool (only first card in indicator shows KPI)
 *   index            - card number within indicator
 */
const ActionTaskCard = ({ action, indicatorLevel = 2, kpi, showKpi = false, index = 0 }) => {
    const [expanded, setExpanded] = useState(false);
    const priority = getPriority(indicatorLevel, action.timeline);
    const cfg = PRIORITY_CONFIG[priority];

    return (
        <div className={`rounded-xl border ${cfg.border} ${cfg.bg} overflow-hidden transition-all`}>
            <div className="p-4">
                {/* Priority badge + action text */}
                <div className="flex items-start gap-3">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2 py-0.5 rounded-full border flex-shrink-0 mt-0.5 ${cfg.badge}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                        {cfg.label}
                    </span>
                    <p className="text-sm text-gray-800 font-medium leading-relaxed flex-1">{action.action}</p>
                </div>

                {/* Timeline + responsibility chips */}
                <div className="flex flex-wrap items-center gap-2 mt-3">
                    {action.timeline && (
                        <span className="inline-flex items-center gap-1.5 text-xs text-gray-500 bg-white border border-gray-200 px-2.5 py-1 rounded-lg">
                            <Clock className="w-3 h-3 text-gray-400" />
                            {action.timeline}
                        </span>
                    )}
                    {action.responsibility && (
                        <span className="inline-flex items-center gap-1.5 text-xs text-gray-500 bg-white border border-gray-200 px-2.5 py-1 rounded-lg">
                            <User className="w-3 h-3 text-gray-400" />
                            {action.responsibility}
                        </span>
                    )}
                    {action.resource && (
                        <button
                            onClick={() => setExpanded(p => !p)}
                            className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors cursor-pointer ml-auto"
                        >
                            Resources {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        </button>
                    )}
                </div>

                {/* Resources (expandable) */}
                {expanded && action.resource && (
                    <div className="mt-3 pt-3 border-t border-white/60">
                        <div className="flex items-start gap-2">
                            <BookOpen className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-gray-600 leading-relaxed">{action.resource}</p>
                        </div>
                    </div>
                )}
            </div>

            {/* KPI — shown once per indicator (on last/first card) */}
            {showKpi && kpi && (
                <div className="px-4 py-3 bg-white/70 border-t border-white/60">
                    <div className="flex items-start gap-2">
                        <Target className="w-3.5 h-3.5 text-[#D67C2C] flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Success KPI</p>
                            <p className="text-xs text-gray-700 leading-relaxed">{kpi}</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ActionTaskCard;
